/**
 * Hero Slider Component with Keen Slider
 * Handles slider initialization, navigation, and autoplay
 */

if (!customElements.get('hero-slider')) {
  class HeroSlider extends HTMLElement {
    constructor() {
      super();
      this.slider = null;
      this.autoplayTimer = null;
      this.autoplayEnabled = this.dataset.autoplay === 'true';
      this.autoplaySpeed = parseInt(this.dataset.autoplaySpeed || '5') * 1000;
      this.autoplayStarted = false;
      this.userInteracted = false;
    }

    connectedCallback() {
      this.initSlider();
      this.attachEventListeners();
      this.setupAutoplayTrigger();
    }

    disconnectedCallback() {
      if (this.slider) {
        this.slider.destroy();
      }
      this.clearAutoplay();
    }

    initSlider() {
      const sliderElement = this.querySelector('.hero-section__slider');
      if (!sliderElement) return;

      const slides = sliderElement.querySelectorAll('.keen-slider__slide');
      if (slides.length <= 1) {
        // Hide navigation if only one slide
        this.hideNavigation();
        return;
      }

      // Initialize Keen Slider
      this.slider = new KeenSlider(sliderElement, {
        loop: true,
        mode: 'snap',
        slides: {
          perView: 1,
          spacing: 0,
        },
        created: () => {
          this.classList.add('slider-initialized');
          this.onSlideChange();
          // Autoplay will be started by setupAutoplayTrigger()
        },
        slideChanged: () => {
          this.onSlideChange();
        },
        dragStarted: () => {
          this.clearAutoplay();
        },
        dragEnded: () => {
          if (this.autoplayEnabled && this.userInteracted) {
            this.startAutoplay();
          }
        },
      });

      // Preload videos in other slides
      this.preloadVideos();
    }

    attachEventListeners() {
      // Previous arrow
      const prevBtn = this.querySelector('.hero-section__arrow--prev');
      if (prevBtn) {
        prevBtn.addEventListener('click', () => {
          if (this.slider) {
            this.slider.prev();
            this.clearAutoplay();
            if (this.autoplayEnabled && this.userInteracted) {
              this.startAutoplay();
            }
          }
        });
      }

      // Next arrow
      const nextBtn = this.querySelector('.hero-section__arrow--next');
      if (nextBtn) {
        nextBtn.addEventListener('click', () => {
          if (this.slider) {
            this.slider.next();
            this.clearAutoplay();
            if (this.autoplayEnabled && this.userInteracted) {
              this.startAutoplay();
            }
          }
        });
      }

      // Indicator dots
      const indicators = this.querySelectorAll('.hero-section__indicator');
      indicators.forEach((indicator) => {
        indicator.addEventListener('click', () => {
          const slideIndex = parseInt(indicator.dataset.slideTo);
          if (this.slider && !isNaN(slideIndex)) {
            this.slider.moveToIdx(slideIndex);
            this.clearAutoplay();
            if (this.autoplayEnabled && this.userInteracted) {
              this.startAutoplay();
            }
          }
        });
      });

      // Pause autoplay on hover
      if (this.autoplayEnabled) {
        this.addEventListener('mouseenter', () => {
          this.clearAutoplay();
        });

        this.addEventListener('mouseleave', () => {
          if (this.userInteracted) {
            this.startAutoplay();
          }
        });
      }

      // Pause autoplay when page is not visible
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          this.clearAutoplay();
        } else if (this.autoplayEnabled && this.userInteracted) {
          this.startAutoplay();
        }
      });
    }

    onSlideChange() {
      if (!this.slider) return;

      const currentIndex = this.slider.track.details.rel;
      
      // Update indicator dots
      const indicators = this.querySelectorAll('.hero-section__indicator');
      indicators.forEach((indicator, index) => {
        if (index === currentIndex) {
          indicator.classList.add('hero-section__indicator--active');
        } else {
          indicator.classList.remove('hero-section__indicator--active');
        }
      });

      // Play/pause videos based on active slide
      this.handleVideoPlayback(currentIndex);
    }

    handleVideoPlayback(activeIndex) {
      const slides = this.querySelectorAll('.keen-slider__slide');
      slides.forEach((slide, index) => {
        const video = slide.querySelector('.hero-section__background-video');
        if (video) {
          if (index === activeIndex) {
            video.play().catch(() => {
              // Autoplay may be prevented by browser
            });
          } else {
            video.pause();
          }
        }
      });
    }

    preloadVideos() {
      // Preload videos in non-active slides for smooth transition
      const videos = this.querySelectorAll('.hero-section__background-video');
      videos.forEach((video, index) => {
        if (index !== 0) {
          // Load video metadata for slides that aren't the first
          setTimeout(() => {
            video.setAttribute('preload', 'metadata');
          }, 1000);
        }
      });
    }

    startAutoplay() {
      this.clearAutoplay();
      if (!this.slider || !this.autoplayEnabled) return;

      this.autoplayTimer = setInterval(() => {
        if (this.slider) {
          this.slider.next();
        }
      }, this.autoplaySpeed);
    }

    clearAutoplay() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }

    hideNavigation() {
      const arrows = this.querySelectorAll('.hero-section__arrow');
      const indicators = this.querySelector('.hero-section__carousel-indicators');
      
      arrows.forEach(arrow => arrow.style.display = 'none');
      if (indicators) {
        indicators.style.display = 'none';
      }
    }

    setupAutoplayTrigger() {
      if (!this.autoplayEnabled) return;

      // Try to start autoplay immediately (works if browser allows it)
      const attemptAutoplay = () => {
        if (!this.userInteracted && this.slider && this.autoplayEnabled) {
          this.userInteracted = true;
          this.startAutoplay();
        }
      };

      // Attempt immediate start (for browsers that allow it)
      setTimeout(attemptAutoplay, 100);

      // Fallback: Start on first user interaction
      const interactionEvents = ['click', 'touchstart', 'keydown', 'scroll', 'mousemove'];
      const handleFirstInteraction = () => {
        if (!this.userInteracted) {
          attemptAutoplay();
          // Remove listeners after first interaction
          interactionEvents.forEach(event => {
            document.removeEventListener(event, handleFirstInteraction);
          });
        }
      };

      interactionEvents.forEach(event => {
        document.addEventListener(event, handleFirstInteraction, { once: true, passive: true });
      });
    }
  }

  customElements.define('hero-slider', HeroSlider);
}
